import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {openInBrowser} from '../../../../../../../../srcCommon/browser/openInBrowser';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../../shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

interface ContactsManagerTableEmptyStateProps {
  title: React.ReactNode;
  image: React.ReactNode;
  description: React.ReactNode;

  learnMoreUrl?: string;
}

/*
 * Style.
 */

const StyledEmptyStateContainerDiv = styled.div`
  position: absolute;
  // Offset for header bars so we do not overlap
  top: 40px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledEmptyStateContentsContainerDiv = styled.div`
  display: flex;
  flex-flow: column;
  text-align: center;
`;

const StyledImgContainerDiv = styled.div`
  margin-bottom: 24px;
`;

const StyledEmptyStateTitleDiv = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.semibold};
`;

const StyledEmptyStateDescriptionDiv = styled.div`
  max-width: 310px;
  color: ${(p) => p.theme.greys.shade70};
  line-height: 21px;
  margin-top: 8px;
`;

const StyledEmptyStateLearnMoreDiv = styled.div`
  display: flex;
  margin-top: 8px;
`;

const StyledLearnMoreButton = styled(Button)`
  margin: auto;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
`;

const StyledLearnMoreIcon = styled(Icon)`
  margin-left: 6px;
`;

const StyledAccountsTableActionsContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;

  &:not(:empty) {
    margin-top: 24px;
  }
`;

/*
 * Component.
 */

export const ContactsManagerTableEmptyState: FC<ContactsManagerTableEmptyStateProps> = (props) => {
  const {title, image, description, learnMoreUrl, children} = props;

  const onLearnMoreClick = () => {
    if (learnMoreUrl) {
      openInBrowser(learnMoreUrl);
    }
  };

  return (
    <StyledEmptyStateContainerDiv>
      <StyledEmptyStateContentsContainerDiv>
        <StyledImgContainerDiv>{image}</StyledImgContainerDiv>
        <StyledEmptyStateTitleDiv>{title}</StyledEmptyStateTitleDiv>
        <StyledEmptyStateDescriptionDiv>{description}</StyledEmptyStateDescriptionDiv>
        {learnMoreUrl && (
          <StyledEmptyStateLearnMoreDiv>
            <StyledLearnMoreButton onClick={onLearnMoreClick} variant={ButtonVariantsEnum.LINK}>
              <StyledButtonContentDiv>
                <Trans id="contacts-manager-empty-state-learn-more">Learn more</Trans>
                <StyledLearnMoreIcon name="externalLinkSmall" />
              </StyledButtonContentDiv>
            </StyledLearnMoreButton>
          </StyledEmptyStateLearnMoreDiv>
        )}

        <StyledAccountsTableActionsContainerDiv>{children}</StyledAccountsTableActionsContainerDiv>
      </StyledEmptyStateContentsContainerDiv>
    </StyledEmptyStateContainerDiv>
  );
};
